/**
 * Turn the guest who just finished a check-in into a patient with an account,
 * and keep everything they typed.
 *
 * ⚠️ THE UID NEVER CHANGES, and every other decision here follows from that.
 * `linkWithCredential` upgrades the anonymous Firebase account in place rather
 * than creating a second one, so:
 *
 *  - `sessionActor.patientUid` on the session is still correct, which means no
 *    PHI is copied between accounts and nothing has to be re-keyed.
 *  - The session stays readable by its own rules branch throughout, so the
 *    answers can be read back and replayed onto the new chart from the client.
 *  - The share code the patient already handed their clinician keeps working. It
 *    resolves through `patientUserId`, which is that same uid.
 *
 * The alternative — sign up a fresh account and move the data across — would need
 * a Cloud Function moving PHI between uids, would break a code already in a
 * clinician's hands, and would leave an orphaned anonymous account holding a copy.
 *
 * ORDER MATTERS. The account is linked first, then the user document, then the
 * chart, then the claim. Each step is only reachable if the one before it worked,
 * and the two cheap-to-repeat steps at the end are best effort: by the time the
 * chart is being written the patient HAS an account, and failing them at that
 * point would mean offering to create one that already exists.
 *
 * WHAT THIS DOES NOT DO is merge into an existing account. A guest whose email is
 * already registered gets told to sign in, and their answers stay on the session
 * for the code they already showed their clinician. Merging needs the admin SDK to
 * reassign `sessionActor`, which the rules forbid outright — see
 * TODO(visitstart-guest-merge) below.
 */

import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/functions";
import firebaseApp, { db } from "services/firebase";
import i18n from "i18n";
import { getVisitStartSession } from "./visitStartSessionService";
import { seedCmrFromVisitStartIntake } from "./visitStartCmrWrites";

const CLAIM_FUNCTION = "claimVisitStartSession";

/**
 * The user document a new patient gets from AccountSetupFlow's buildBaseUserDoc.
 *
 * Deliberately identical, field for field. A patient who arrives through the
 * check-in must be indistinguishable from one who signed up at /auth — a missing
 * `onboarding` sends them to the account-setup gate to pick a role they already
 * have, and a missing `betaAccess` locks them out of the closed beta they were
 * just invited into. The rules additionally REQUIRE uid, email, name, lastName
 * and role at create, so four of these are not optional.
 */
export const buildPatientUserDoc = ({ uid, email }) => ({
  uid,
  email: email || null,
  name: "",
  lastName: "",
  role: "patient",
  registrationDate: firebase.firestore.FieldValue.serverTimestamp(),
  onboarding: false,
  // [BETA-FORCE] Mirrors AccountSetupFlow. Remove with that one when the closed
  // beta ends, not before, or check-in signups become the only non-beta accounts.
  betaAccess: true,
});

/** Firebase's codes, in the words of someone standing in a waiting room. */
const friendlyAuthError = (err) => {
  switch (err?.code) {
    case "auth/email-already-in-use":
    case "auth/credential-already-in-use":
    case "auth/account-exists-with-different-credential":
      return i18n.t("visitStart:accountErrors.emailInUse");
    case "auth/weak-password":
      return i18n.t("visitStart:accountErrors.weakPassword");
    case "auth/invalid-email":
      return i18n.t("visitStart:accountErrors.invalidEmail");
    case "auth/operation-not-allowed":
      return i18n.t("visitStart:accountErrors.signUpDisabled");
    case "auth/requires-recent-login":
      return i18n.t("visitStart:accountErrors.recentLogin");
    default:
      return err?.message || i18n.t("visitStart:accountErrors.generic");
  }
};

/**
 * Send the verification email, and never let its failure look like a failed
 * signup. The account exists at this point; an unsent email costs the patient one
 * tap on "resend" from the dashboard, which is where they land anyway.
 */
const sendVerification = async (user) => {
  try {
    await user.sendEmailVerification();
    return true;
  } catch (err) {
    console.warn("[VisitStart] verification email failed", err);
    return false;
  }
};

/**
 * Stop the TTL policy deleting the session now that it has an owner who can sign
 * back in. Best effort by design: if it fails the chart is already seeded, so the
 * patient keeps their medications, allergies and demographics — only the
 * visit-specific answers expire, on the schedule they were already on.
 */
const claimSession = async (session) => {
  if (!session?.sessionId) return false;
  try {
    const callable = firebaseApp.functions().httpsCallable(CLAIM_FUNCTION);
    // locationId travels with the id because the callable has two collections to
    // look in and the id alone does not say which. It is not a permission — the
    // function still checks the session's own sessionActor against the caller.
    await callable({
      sessionId: session.sessionId,
      locationId: session.locationId || null,
    });
    return true;
  } catch (err) {
    console.warn("[VisitStart] session claim failed", err);
    return false;
  }
};

/**
 * @param {{ email: string, password: string, session?: object }} args
 * @returns {Promise<{
 *   uid: string,
 *   seededFields: string[],
 *   failedFields: string[],
 *   claimed: boolean,
 *   verificationSent: boolean,
 * }>}
 * @throws {Error} with a message written for the patient, not the console.
 */
export async function upgradeVisitStartGuestToAccount({
  email,
  password,
  session,
} = {}) {
  const cleanEmail = String(email || "").trim();
  if (!cleanEmail || !password) {
    throw new Error(i18n.t("visitStart:accountErrors.missingFields"));
  }

  const current = firebaseApp.auth().currentUser;
  if (!current) {
    throw new Error(i18n.t("visitStart:accountErrors.sessionExpired"));
  }
  // Guard rather than assume. This is only offered to guests, but the offer sits
  // on a screen a signed-in patient also reaches, and linking a credential onto a
  // real account is a different operation with a different failure mode.
  if (!current.isAnonymous) {
    throw new Error(i18n.t("visitStart:accountErrors.alreadySignedIn"));
  }

  const credential = firebase.auth.EmailAuthProvider.credential(
    cleanEmail,
    password,
  );

  let upgraded;
  try {
    // TODO(visitstart-guest-merge): when the email is already registered this is
    // where a merge would begin, and it cannot be done from here — the rules
    // forbid reassigning sessionActor, so moving the intake onto the existing
    // account needs an admin-SDK callable that verifies both sides.
    const result = await current.linkWithCredential(credential);
    upgraded = result?.user || firebaseApp.auth().currentUser;
  } catch (err) {
    throw new Error(friendlyAuthError(err));
  }

  const uid = upgraded?.uid;
  if (!uid) {
    throw new Error(i18n.t("visitStart:accountErrors.generic"));
  }

  // From here the account EXISTS. Nothing below may throw, or the patient is told
  // their signup failed while holding a working account, and every retry then
  // fails on auth/email-already-in-use.
  try {
    await db
      .collection("users")
      .doc(uid)
      .set(buildPatientUserDoc({ uid, email: upgraded.email || cleanEmail }));
  } catch (err) {
    // The one exception, because a patient with no user document is bounced
    // between /onboarding and /auth forever. Surfacing it beats a silent
    // half-account, and the account itself is recoverable by signing in.
    console.warn("[VisitStart] user doc create failed", err);
    throw new Error(i18n.t("visitStart:accountErrors.setupIncomplete"));
  }

  let seededFields = [];
  let failedFields = [];
  try {
    const intake = await getVisitStartSession(session);
    const result = await seedCmrFromVisitStartIntake({
      patientUid: uid,
      elementAnswers: intake?.elementAnswers || {},
    });
    seededFields = result.appliedFields;
    failedFields = result.failedFields;
  } catch (err) {
    // Reading the session failed, so nothing was seeded. The account is fine and
    // the answers are still on the session — a retry is a data-entry job, not a
    // signup one, which is why this does not throw.
    console.warn("[VisitStart] could not read the intake to seed it", err);
    failedFields = ["medications", "allergies", "demographics"];
  }

  const claimed = await claimSession(session);
  const verificationSent = await sendVerification(upgraded);

  return { uid, seededFields, failedFields, claimed, verificationSent };
}
